import { Cause, Effect, Option, Predicate, Result } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';
import type { Cart as CartView } from '@vs-shop/engine/capability/cart/cartSchema';
import { releaseToken } from '@vs-shop/engine/capability/cart/cartToken';
import { readHeldCartSnapshot } from '@vs-shop/engine/capability/cart/readCurrentCart';

import { getOrderExpanded } from '../../bind/orderRequests';
import { CART_READ_TIMEOUT, cartReadRetryPolicy } from '@vs-shop/engine/module/cart/cartTransport';
import { OrderReadFailedError } from '../../data/error/orderClient';
import { toCartResult } from '../../capability/cart/CartLive';
import type { OrderDetail } from '../../schema/openapi/backend';
import { getOrderToken } from '../../utils/cookies/getOrderToken';

/**
 * Raw browser-side counterpart to `getOrderHydrationData`: order atoms execute
 * this in the browser, where the TenantServer port global is never filled.
 *
 * Only a `404` proves the cart is gone, so only a `404` removes the token. A
 * transport failure or a gateway status is retried, and a failure that remains
 * propagates with the token intact: the atom keeps its last known cart, and the
 * next read can still reach it.
 */
const getOrder = readHeldCartSnapshot({
  token: getOrderToken,
  read: (token) =>
    getOrderExpanded(token).pipe(
      Effect.timeout(CART_READ_TIMEOUT),
      Effect.retry(cartReadRetryPolicy),
      Effect.catchTag('@app/BackendApi/Order/GetOrderExpanded/ApiRequest', (error) =>
        Effect.fail(new OrderReadFailedError({ status: error.status, cause: error }))
      )
    ),
  isMissing: Predicate.isTagged('@app/BackendApi/Order/GetOrderExpanded/NotFound'),
  onMissing: releaseToken,
}).pipe(Effect.map((snapshot) => snapshot.value));

export default getOrder;

/** Project the confirmed Cart from the same raw result used by checkout. */
export const cartFromOrderResult = <E>(
  result: AsyncResult.AsyncResult<Option.Option<OrderDetail>, E>
) =>
  AsyncResult.flatMap(
    result,
    (order, previous): AsyncResult.AsyncResult<Option.Option<CartView>, unknown> =>
      Option.match(order, {
        onNone: () => AsyncResult.success(Option.none<CartView>(), previous),
        onSome: (detail) =>
          Result.match(toCartResult(detail), {
            onSuccess: (cart) => AsyncResult.success(Option.some(cart), previous),
            onFailure: (error) => AsyncResult.failure(Cause.fail(error)),
          }),
      })
  );

/** Cart hydration still mounts the order dependency; a Cart refresh reads the same order. */
export const makeCartAtom = <E>(
  orderAtom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<OrderDetail>, E>>
) => {
  const cartViewAtom = Atom.map(orderAtom, cartFromOrderResult);
  return Atom.transform(
    cartViewAtom,
    (get, cart) => {
      get(orderAtom);
      return get(cart);
    },
    { initialValueTarget: cartViewAtom }
  );
};
